import * as Dialog from "@radix-ui/react-dialog"
import { Download, Loader2, X } from "lucide-react"
import { useState } from "react"
import { StudioQualityMarkToggleRow, type StudioQualityValue } from "@/components/studio-quality-mark"
import { SingleClipPlayer } from "@/components/timeline-player"
import { Button } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { downloadImage, filenameFromUrl } from "@/lib/download-image"

export type VideoVersionPreviewVersion = {
  id: number
  name?: string | null
  created_at: string
  preview_url: string | null
  download_url?: string | null
  stream_url?: string | null
  active: boolean
  studio_quality?: StudioQualityValue
  duration_ms?: number | null
  frame_rate?: number | null
  timeline_frame_urls?: string[]
  timeline_frame_aspect_ratio?: number | null
}

type Props = {
  version: VideoVersionPreviewVersion
  open: boolean
  onOpenChange: (open: boolean) => void
  activating: boolean
  onActivate: (versionId: number) => Promise<boolean>
  qualitySaving: boolean
  onSaveQuality: (versionId: number, next: StudioQualityValue) => void
}

function formatVersionDate(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export function VideoVersionPreviewDialog({
  version,
  open,
  onOpenChange,
  activating,
  onActivate,
  qualitySaving,
  onSaveQuality,
}: Props) {
  const { t } = useTranslation(["shot", "common"])
  const [submitting, setSubmitting] = useState(false)

  // Same-origin proxy (stream_url/download_url): плеер ставит crossOrigin=anonymous;
  // rails_blob preview_url уводит на S3 без CORS для нашего origin.
  const streamUrl = version.stream_url ?? version.download_url ?? version.preview_url

  const title = version.name?.trim() || `v${version.id}`
  const busy = submitting || activating
  const canMakeCurrent = !version.active && !busy

  const handleMakeCurrent = async () => {
    if (!canMakeCurrent) return
    setSubmitting(true)
    try {
      const ok = await onActivate(version.id)
      if (ok) onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDownload = () => {
    if (!version.download_url) return
    void downloadImage(version.download_url, filenameFromUrl(version.download_url, `video_v${version.id}`, ".mp4"))
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-dialog-overlay fixed inset-0 z-[110] bg-[var(--ui-overlay-heavy)] backdrop-blur-sm" />
        <Dialog.Content className="ui-dialog-content fixed top-1/2 left-1/2 z-[111] flex max-h-[90vh] w-[min(960px,94vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg border border-ui-border shadow-2xl shadow-black/50 focus:outline-none dark:bg-zinc-950 light:bg-ui-bg-elevated">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-ui-border px-4 py-3">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-sm font-semibold text-ui-text-primary">
                {t("mediaVersions.previewTitle", { name: title })}
              </Dialog.Title>
              <p className="mt-0.5 text-[11px] text-ui-text-muted tabular-nums">
                {formatVersionDate(version.created_at)}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <StudioQualityMarkToggleRow
                value={version.studio_quality ?? null}
                disabled={qualitySaving || busy}
                dense
                onSet={(next) => onSaveQuality(version.id, next)}
              />
              {version.download_url ? (
                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-ui-border bg-ui-surface-raised px-2.5 py-1.5 text-xs text-ui-text-primary transition-colors hover:bg-ui-surface-raised"
                  title={t("common.downloadOriginal")}
                >
                  <Download className="h-4 w-4" />
                  {t("common.downloadOriginal")}
                </button>
              ) : null}
              <Button type="button" size="sm" disabled={!canMakeCurrent} onClick={() => void handleMakeCurrent()}>
                {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                {version.active ? t("mediaVersions.current") : t("mediaVersions.makeCurrent")}
              </Button>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded-lg p-1 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
                  aria-label={t("common.close")}
                >
                  <X className="h-4 w-4" />
                </button>
              </Dialog.Close>
            </div>
          </div>

          <div className="it2tv-scrollbar min-h-0 flex-1 overflow-y-auto p-3">
            {streamUrl ? (
              <SingleClipPlayer
                sourceId={version.id}
                streamUrl={streamUrl}
                durationMs={version.duration_ms}
                frameRate={version.frame_rate}
                aspectRatio={version.timeline_frame_aspect_ratio}
                timelineFrameUrls={version.timeline_frame_urls}
                active={open}
                className="w-full max-w-full"
              />
            ) : (
              <p className="py-12 text-center text-sm text-ui-text-muted">{t("mediaVersions.noPreview")}</p>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
